/**
 * Sentry: inicialización y reporte de errores (Paso 5 escalabilidad).
 * Solo activo cuando VITE_SENTRY_DSN está definido (ej. en producción).
 */

import * as Sentry from '@sentry/react';
import { isBenignTelnyxSdkError } from '@/lib/telnyxWebRTCStore';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const isProd = import.meta.env.PROD;

if (dsn && typeof dsn === 'string' && dsn.length > 0) {
  Sentry.init({
    dsn,
    environment: isProd ? 'production' : 'development',
    release: import.meta.env.VITE_APP_VERSION as string | undefined,
    sampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      if (import.meta.env.DEV) return null;
      // H1 I4: descartar errores Telnyx benignos (call leg cerrado, etc.) — son
      // ruido normal post-fin-de-llamada, no bugs reportables.
      const errMsg =
        (hint?.originalException instanceof Error ? hint.originalException.message : '')
        || event.message
        || event.exception?.values?.[0]?.value
        || '';
      if (errMsg && isBenignTelnyxSdkError(errMsg)) return null;
      return event;
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay: 5% de sesiones normales + 100% de sesiones que tuvieron error.
      // Mask de inputs/text para no capturar PII (override con `unmask` por elemento).
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: isProd ? 0.1 : 0,
    replaysSessionSampleRate: isProd ? 0.05 : 0,
    replaysOnErrorSampleRate: isProd ? 1.0 : 0,
  });
}

/** Reportar un error manualmente (ej. desde ErrorBoundary). No-op si Sentry no está configurado. */
export function reportError(error: Error, context?: { componentStack?: string; extra?: Record<string, unknown> }): void {
  if (!dsn) return;
  Sentry.withScope((scope) => {
    if (context?.componentStack) scope.setContext('react', { componentStack: context.componentStack });
    if (context?.extra) Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
    Sentry.captureException(error);
  });
}

/** Identificar al usuario actual en Sentry (tenant, rol). Llamar tras resolver auth. */
export function setSentryUser(user: { id: string; tenantId: string; role: string; email?: string }): void {
  if (!dsn) return;
  Sentry.setUser({ id: user.id, email: user.email });
  Sentry.setTag('tenant_id', user.tenantId);
  Sentry.setTag('role', user.role);
}

/** Limpiar identidad de Sentry (logout). */
export function clearSentryUser(): void {
  if (!dsn) return;
  Sentry.setUser(null);
}

export { Sentry };
